import { CMSEPGItem } from '@/types/CMSEPGItem';
import Request from './request';
import { CMSItemResponse, CMSPaginatedResponse, CurrentLiveStreamsResponse, RailPageResponse, SearchResponse, SeriesOrVideoResponse, SeriesResponse, TG4Region, VideoResponseWithProgress } from '@/types/cms';
import { CMSVideo } from '@/types/Video';
import {
  CategoryDetailResponse,
  CategoryResponseWithSubCategories,
  PopulatedSeasonResponse,
  RecommendationResponse,
  SingleSeriesResponse,
  SingleVideoResponse,
  TG4Site,
} from '@/types/cms';
import { APILocation } from '@/types/APILocation';


interface VideoOptions {
  site?: TG4Site
  includeNextVideo?: boolean
  /** Requires a stored Peach token — the API rejects the request otherwise. */
  loadUserProgress?: boolean
}

interface SeasonOptions {
  site?: TG4Site
  /** Requires a stored Peach token — the API rejects the request otherwise. */
  loadUserProgress?: boolean
}

/** Both user list endpoints require a stored Peach token. */
interface UserHistoryOptions {
  limit: number
  /** From the previous page's `nextPageLastItemId`. Omitted for the first page. */
  lastItemId?: string
  /** From the previous page's `nextPageLastItemDate`. Omitted for the first page. */
  lastItemDate?: number
}

interface UserBookmarksOptions {
  limit: number
  /** Number of items already held. Omitted for the first page. */
  skip?: number
}

export default class CMS {

  private get baseUrl(): string {
    if (process.env.NODE_ENV === 'production') {
      return 'https://api.tg4dev.com/api';
    }
    return 'https://api.tg4dev.com/api';
  }

  private locationListener?: (location: APILocation) => void;

  private request = new Request(
    { 'X-TG4-Platform': 'SmartTV' },
    () => {

      let isLoggedIn = 'false';
      let token: string | null = null;
      if (typeof localStorage !== 'undefined') {
        try {
          token = localStorage.getItem('token');
          if (token) {
            isLoggedIn = 'true';
          }
        } catch {
          // Ignore localStorage access errors and keep default 'false'
        }
      }

      const headers: Record<string, string> = {
        'X-TG4-User-Logged-In': isLoggedIn
      };

      // Forwarded to Peach by the API for endpoints that can return watch progress
      if (token) {
        headers['X-Peach-Authorization'] = `Bearer ${token}`;
        headers['X-Peach-Token-Type'] = 'cpa';
      }

      return headers;
    },
    (response) => this.handleLocationHeaders(response)
  );


  onLocationChange(listener: (location: APILocation) => void) {
    this.locationListener = listener;
  }

  private handleLocationHeaders(response: Response) {
    if (!this.locationListener) return;

    const rawRegion = response.headers.get('x-tg4-region');
    let region: TG4Region | null = null;
    if (rawRegion === 'IOI') {
      region = TG4Region.Ireland;
    } else if (rawRegion === 'WW') {
      region = TG4Region.WorldWide;
    }

    if (!region) {
      console.warn('Unknown region header:', rawRegion);
      return;
    }

    const rawCountryCode = response.headers.get('x-tg4-country-code');
    const rawCity = response.headers.get('x-tg4-city');
    let city: string | null = null;
    let countryCode: string | null = null;
    try {
      city = rawCity ? decodeURIComponent(rawCity) : null;
      countryCode = rawCountryCode ? decodeURIComponent(rawCountryCode) : null;
    } catch {
      console.warn('Error decoding location headers:', rawCity, rawCountryCode);
      // Ignore URI decoding errors and keep default null values
    }

    this.locationListener({
      region,
      countryCode: countryCode,
      city: city
    });
  }

  /** Builds a `?a=1&b=2` string, skipping undefined values. Returns '' when empty. */
  private query(params: Record<string, string | number | boolean | undefined>): string {
    const search = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined) {
        search.append(key, String(value));
      }
    });
    const queryString = search.toString();
    return queryString ? `?${queryString}` : '';
  }

  async getLiveStreams(): Promise<CurrentLiveStreamsResponse> {
    return await this.request.get<CurrentLiveStreamsResponse>(`${this.baseUrl}/epg/current`);
  }


  async getEPG(channel: string): Promise<CMSEPGItem[]> {
    return await this.request.get<CMSEPGItem[]>(`${this.baseUrl}/epg/${channel}`);
  }

  async getNextVideoForId(videoId: string): Promise<CMSVideo> {
    return await this.request.get<CMSVideo>(`${this.baseUrl}/series/upnext/bcov/${videoId}`);
  }

  /**
   * Homepage rails. The rail page returned depends on the caller's region,
   * which the API resolves from the request — there is no location parameter.
   */
  async getHomeRails(): Promise<RailPageResponse> {
    const url = `${this.baseUrl}/rails/home${this.query({ isSmartTV: true })}`;
    return await this.request.get<RailPageResponse>(url);
  }

  /**
   * Discover page rails. Region is resolved from the request just as it is for
   * the home rails, so there is no location parameter here either.
   */
  async getDiscoverRails(): Promise<RailPageResponse> {
    const url = `${this.baseUrl}/rails/discover${this.query({ isSmartTV: true })}`;
    return await this.request.get<RailPageResponse>(url);
  }

  /**
   * Every top-level category, each carrying its own subcategories.
   *
   * Returned as a bare array rather than wrapped in an envelope, which is why
   * this is the one CMS call with no `*Response` type of its own.
   */
  async getCategories(site: TG4Site = 'TG4'): Promise<CategoryResponseWithSubCategories[]> {
    const url = `${this.baseUrl}/categories${this.query({ site })}`;
    return await this.request.get<CategoryResponseWithSubCategories[]>(url);
  }

  /**
   * Everything a category page is built from: the category itself with its
   * subcategories, its parent categories, and its rails.
   *
   */
  async getCategory(slug: string, site: TG4Site = 'TG4'): Promise<CategoryDetailResponse> {
    const url = `${this.baseUrl}/categories/${encodeURIComponent(slug)}${this.query({ site, isSmartTV: true  })}`;
    return await this.request.get<CategoryDetailResponse>(url);
  }

  /**
   * One page of every series in a category, ordered by the API and returned as
   * a bare array. `limit` is capped at 100 server side.
   *
   * Note the parameter is `sites` here, plural, where `/categories` above takes
   * a singular `site`. Both are the same `TG4Site` value.
   */
  async getSeriesForCategory(
    slug: string,
    skip: number,
    limit: number,
    site: TG4Site = 'TG4'
  ): Promise<SeriesResponse[]> {
    const url = `${this.baseUrl}/series${this.query({
      sites: site,
      category: slug,
      skip,
      limit
    })}`;
    return await this.request.get<SeriesResponse[]>(url);
  }

  /** Series details plus its seasons and categories. Seasons are newest first. */
  async getSeries(slug: string, site?: TG4Site): Promise<SingleSeriesResponse> {
    const url = `${this.baseUrl}/series/${encodeURIComponent(slug)}${this.query({ site })}`;
    return await this.request.get<SingleSeriesResponse>(url);
  }

  /** Episodes for one season of a series, ordered by the series' render type. */
  async getSeason(slug: string, seasonNumber: number, options: SeasonOptions = {}): Promise<PopulatedSeasonResponse> {
    const url = `${this.baseUrl}/series/${encodeURIComponent(slug)}/${seasonNumber}${this.query({
      site: options.site,
      loadUserProgress: options.loadUserProgress
    })}`;
    return await this.request.get<PopulatedSeasonResponse>(url);
  }

  /** A single episode by its vid, with optional series, season, categories and next video. */
  async getVideo(vid: string, options: VideoOptions = {}): Promise<SingleVideoResponse> {
    const url = `${this.baseUrl}/videos/${encodeURIComponent(vid)}${this.query({
      site: options.site,
      includeNextVideo: options.includeNextVideo,
      loadUserProgress: options.loadUserProgress
    })}`;
    return await this.request.get<SingleVideoResponse>(url);
  }

  /** A single episode addressed by series slug plus season and episode number. */
  async getVideoBySlug(slug: string, seasonNumber: number, episodeNumber: number, options: VideoOptions = {}): Promise<SingleVideoResponse> {
    const url = `${this.baseUrl}/videos/slug/${encodeURIComponent(slug)}/${seasonNumber}/${episodeNumber}${this.query({
      site: options.site,
      includeNextVideo: options.includeNextVideo,
      loadUserProgress: options.loadUserProgress
    })}`;
    return await this.request.get<SingleVideoResponse>(url);
  }

  /**
   * Basic search: one mixed, API-ordered list of series, videos and categories.
   *
   * There is no paging — the endpoint returns the whole result set — and no
   * filters. The filtered `/search/advanced` endpoint the phone apps use is
   * deliberately not wrapped here: neither TV build exposes filters.
   */
  async search(query: string, site: TG4Site = 'TG4'): Promise<SearchResponse> {
    const url = `${this.baseUrl}/search${this.query({ query, site })}`;
    return await this.request.get<SearchResponse>(url);
  }

  /**
   * One page of the signed-in user's watch history, newest watched first.
   *
   * Paged by cursor rather than offset: hand the previous page's
   * `nextPageLastItemId` and `nextPageLastItemDate` straight back in. A cursor
   * page can repeat an item that moved, so callers dedupe as they append.
   */
  async getUserHistory(options: UserHistoryOptions): Promise<CMSPaginatedResponse<VideoResponseWithProgress[]>> {
    const url = `${this.baseUrl}/user/history${this.query({
      limit: options.limit,
      lastItemId: options.lastItemId,
      lastItemDate: options.lastItemDate
    })}`;
    return await this.request.get<CMSPaginatedResponse<VideoResponseWithProgress[]>>(url);
  }

  /**
   * One page of the signed-in user's bookmarks: series and videos mixed, in the
   * order the API returns them. Offset paged, unlike the history above.
   */
  async getUserBookmarks(options: UserBookmarksOptions): Promise<CMSPaginatedResponse<CMSItemResponse[]>> {
    const url = `${this.baseUrl}/user/bookmarks${this.query({
      limit: options.limit,
      skip: options.skip
    })}`;
    return await this.request.get<CMSPaginatedResponse<CMSItemResponse[]>>(url);
  }

  async continueWatchingRail(): Promise<VideoResponseWithProgress[]> {
    const url = `${this.baseUrl}/user/continue-watching`;
    return await this.request.get<VideoResponseWithProgress[]>(url);
  }

  /**
   * A random playable series or episode, which is what a `LUCKY_DIP` rail's
   * tiles fetch when pressed: the tiles themselves carry no destination, so the
   * pick is made server side at press time.
   */
  async getLuckyDip(): Promise<SeriesOrVideoResponse> {
    const url = `${this.baseUrl}/recommendations/luckydip`;
    return await this.request.get<SeriesOrVideoResponse>(url);
  }

  async forYouRail(userId: number): Promise<SeriesResponse[]> {
    const url = `${this.baseUrl}/recommendations/user/${encodeURIComponent(userId)}/foryou`;
    return await this.request.get<SeriesResponse[]>(url);
  }

  /**
   * Episodes the recommender relates to one episode, which is what the episode
   * page's rail shows. The `id` names the recommendation set rather than any
   * video: the other platforms report it back to Peach, which this app does not.
   */
  async getRelatedVideos(vid: string): Promise<RecommendationResponse> {
    const url = `${this.baseUrl}/recommendations/video/${encodeURIComponent(vid)}/related`;
    return await this.request.get<RecommendationResponse>(url);
  }
}
